import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Hotel, Loader2 } from "lucide-react";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return setError("Missing invitation token.");
    api
      .get(`/auth/invite/${token}`)
      .then((r) => {
        setInvite(r.data);
        setName(r.data.name || "");
      })
      .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/accept-invite", { token, password, name });
      setUser(data);
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center bg-primary text-primary-foreground">
            <Hotel className="h-5 w-5" />
          </div>
          <span className="font-heading text-lg font-black tracking-tight">GCG HOTELS</span>
        </div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Accept Invitation</h1>
        {invite ? (
          <p className="mt-2 text-sm text-muted-foreground">
            Set a password for <span className="font-medium text-foreground">{invite.email}</span>.
          </p>
        ) : (
          !error && <div className="mt-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
        )}

        {invite && (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="accept-invite-form">
            <div className="space-y-1.5">
              <Label>Full Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="invite-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="invite-password" />
            </div>
            {error && <p className="text-sm text-destructive">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading} data-testid="invite-submit">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Activate Account
            </Button>
          </form>
        )}
        {error && !invite && (
          <div className="mt-6">
            <p className="text-sm text-destructive" data-testid="invite-error">{error}</p>
            <Link to="/login" className="mt-4 inline-block text-sm text-primary hover:underline">Back to sign in</Link>
          </div>
        )}
      </div>
    </div>
  );
}
